import { useState } from "react";
import { Activity, Loader2, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

// Google's Core Web Vitals thresholds. Each metric maps a raw value to one of
// three ratings; lower is better for all of these.
const RATING = {
  good: { label: "Good", color: "#00C853", Icon: CheckCircle2 },
  ni: { label: "Needs Work", color: "#FFD600", Icon: AlertTriangle },
  poor: { label: "Poor", color: "#FF3B30", Icon: XCircle },
};

function rate(value, goodMax, niMax) {
  if (value == null) return null;
  if (value <= goodMax) return "good";
  if (value <= niMax) return "ni";
  return "poor";
}

function ms(v) {
  if (v == null) return "—";
  return v >= 1000 ? `${(v / 1000).toFixed(2)}s` : `${Math.round(v)}ms`;
}

function MetricCard({ label, display, rating, threshold, testid }) {
  const r = rating ? RATING[rating] : null;
  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="uppercase-label">{label}</span>
        {r && <r.Icon className="w-5 h-5" style={{ color: r.color }} strokeWidth={1.75} />}
      </div>
      <div
        className="font-heading text-4xl font-black tracking-tighter mt-2"
        style={r ? { color: r.color } : {}}
      >
        {display}
      </div>
      <div className="flex items-center justify-between mt-2">
        {r ? (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${r.color}1A`, color: r.color }}>
            {r.label}
          </span>
        ) : (
          <span className="text-xs text-gray-400">No data</span>
        )}
        <span className="text-xs text-gray-400">{threshold}</span>
      </div>
    </div>
  );
}

function SecondaryStat({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg px-4 py-3">
      <div className="uppercase-label">{label}</div>
      <div className="font-heading text-xl font-black tracking-tighter mt-0.5">{value}</div>
    </div>
  );
}

export default function CoreWebVitals({ reportId, initialData }) {
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data: v } = await api.get(`/reports/${reportId}/vitals`);
      setData(v);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error("Could not load Core Web Vitals", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  const lcpRating = data && rate(data.largest_contentful_paint_ms, 2500, 4000);
  const fidRating = data && rate(data.first_input_delay_ms, 100, 300);
  const clsRating = data && rate(data.cumulative_layout_shift, 0.1, 0.25);

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 animate-fade-up" data-testid="core-web-vitals">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-klein" />
          <h3 className="font-heading text-xl font-bold tracking-tight">Core Web Vitals</h3>
        </div>
        {data && (
          <Button variant="outline" onClick={run} disabled={loading} className="border-gray-300" data-testid="vitals-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-run"}
          </Button>
        )}
      </div>

      {!data ? (
        <div className="text-center py-8">
          <p className="text-gray-500 max-w-md mx-auto text-sm">
            Run a real browser-rendered check of this page to measure how Google actually sees its
            loading speed and visual stability — the metrics that affect ranking.
          </p>
          <Button onClick={run} disabled={loading} className="mt-5 bg-klein hover:bg-blue-800 text-white" data-testid="vitals-run">
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running check… (~30s)</>
            ) : (
              "Run Core Web Vitals check"
            )}
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <MetricCard
              label="LCP"
              display={ms(data.largest_contentful_paint_ms)}
              rating={lcpRating}
              threshold="Good ≤ 2.5s"
              testid="vital-lcp"
            />
            <MetricCard
              label="FID"
              display={ms(data.first_input_delay_ms)}
              rating={fidRating}
              threshold="Good ≤ 100ms"
              testid="vital-fid"
            />
            <MetricCard
              label="CLS"
              display={data.cumulative_layout_shift != null ? data.cumulative_layout_shift.toFixed(3) : "—"}
              rating={clsRating}
              threshold="Good ≤ 0.1"
              testid="vital-cls"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <SecondaryStat label="On-Page Score" value={data.onpage_score != null ? Math.round(data.onpage_score) : "—"} />
            <SecondaryStat label="Time to Interactive" value={ms(data.time_to_interactive_ms)} />
            <SecondaryStat label="DOM Complete" value={ms(data.dom_complete_ms)} />
            <SecondaryStat
              label="Page Weight"
              value={data.total_transfer_size_bytes != null ? `${Math.round(data.total_transfer_size_bytes / 1024)}KB` : "—"}
            />
          </div>
        </>
      )}
    </div>
  );
}
